import { openaiApi } from '../../../shared/api/openai.api';
import type { CommentFilter, SeedingComment } from './pick-comments';

/**
 * Отсев негатива перед блоком «Топовые комментарии под роликом».
 *
 * Артур просил, чтобы негативные комментарии, насмешки и критика в отчёт не
 * попадали. Раньше порядок был чисто по лайкам, а самый залайканный под
 * новостью — далеко не всегда доброжелательный.
 *
 * Спрашиваем не про весь список, а про голову: в блок идут три комментария, и
 * платить за разбор трёх сотен, чтобы выбрать три, незачем. Запас нужен на
 * случай, если половину отсеют.
 *
 * В отчёт идут только проверенные комментарии. Сбой проверки и пустой отбор
 * не возвращают исходный список; непроверенный хвост также не включается.
 */

/** Сколько кандидатов показываем модели на каждый комментарий в блоке. */
const LOOKAHEAD = 5;

export type ScreenDeps = {
  screen: (comments: Array<{ id: string; text: string }>) => Promise<{ keep: string[] | null }>;
};

export function makeCommentFilter(
  count: number,
  deps: ScreenDeps = { screen: (c) => openaiApi.screenComments(c) },
  onFailure: () => void = () => {},
): CommentFilter {
  return async (comments: SeedingComment[]): Promise<SeedingComment[]> => {
    if (comments.length === 0 || count <= 0) return [];
    const head = comments.slice(0, count * LOOKAHEAD);

    let keep: string[] | null;
    try {
      ({ keep } = await deps.screen(head.map((c) => ({ id: c.id, text: c.text }))));
    } catch (err) {
      console.warn('[Отработано] проверка комментариев не завершилась:', err);
      onFailure();
      return [];
    }
    if (!Array.isArray(keep) || keep.some((id) => typeof id !== 'string')) {
      onFailure();
      return [];
    }

    const allowed = new Set(keep);
    return head.filter((c) => allowed.has(c.id));
  };
}
